import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { login, resetPassword, bootstrapFirstAdmin, isPasswordRecovery, updateRecoveredPassword } = useAuth();

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage('');
    const result = await login(email.trim(), password);
    if (!result.success) {
      setMessage(result.error || 'Authentication failed');
      setShowBootstrap(result.code === 'not_admin');
    }
    setIsLoading(false);
  };

  const handleReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    const result = await resetPassword(email.trim());
    setMessage(result.success
      ? 'If that account exists, a password reset email has been sent.'
      : result.error || 'Unable to send password reset email.');
    setIsLoading(false);
  };

  const handleBootstrap = async () => {
    setIsLoading(true);
    const result = await bootstrapFirstAdmin(bootstrapToken);
    setMessage(result.success
      ? 'First administrator activated. The dashboard is loading.'
      : result.error || 'Unable to activate the first administrator.');
    setIsLoading(false);
    if (result.success) window.location.reload();
  };

  const handleRecoveredPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 6) {
      setMessage('Password must be at least 6 characters.');
      return;
    }
    setIsLoading(true);
    const result = await updateRecoveredPassword(password);
    setMessage(result.success ? 'Password updated. Please sign in.' : result.error || 'Password update failed.');
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground">Admin Access</h2>
          <p className="mt-2 text-muted-foreground">
            {showReset ? 'Reset your administrator password' : 'Sign in to your admin dashboard'}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={isPasswordRecovery ? handleRecoveredPassword : showReset ? handleReset : handleLogin}>
          <div className="space-y-4">
            <label className="block text-sm font-medium text-foreground">
              Email address
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 w-full px-3 py-2 border border-border rounded-md bg-background"
              />
            </label>
            {(!showReset || isPasswordRecovery) && (
              <label className="block text-sm font-medium text-foreground">
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-border rounded-md bg-background"
                />
              </label>
            )}
          </div>

          {message && <div className="text-sm text-center bg-muted p-3 rounded-md">{message}</div>}

          <button type="submit" disabled={isLoading} className="w-full py-2 px-4 rounded-md text-white bg-primary disabled:opacity-50">
            {isLoading ? 'Please wait…' : isPasswordRecovery ? 'Set new password' : showReset ? 'Send reset email' : 'Sign in'}
          </button>

          {!isPasswordRecovery && (
            <button type="button" onClick={() => { setShowReset(!showReset); setMessage(''); }} className="w-full text-sm underline">
              {showReset ? 'Back to sign in' : 'Forgot password?'}
            </button>
          )}
        </form>

        {showBootstrap && !showReset && (
          <div className="border border-border rounded-md p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              No admin grant exists for this authenticated account. If this is the first administrator, enter the one-time bootstrap token.
            </p>
            <input
              type="password"
              value={bootstrapToken}
              onChange={(event) => setBootstrapToken(event.target.value)}
              placeholder="One-time bootstrap token"
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            />
            <button type="button" disabled={!bootstrapToken || isLoading} onClick={handleBootstrap} className="w-full py-2 px-4 rounded-md text-white bg-primary disabled:opacity-50">
              Activate first administrator
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
